/** The realtime switches the AI socket accepts. A misspelled key closes the socket. */
export const REALTIME_FEATURE_NAMES = [
  'mainContent',
  'keyPoints',
  'confirmItems',
  'enhanced',
  'assist',
] as const

export type RealtimeFeatureName = (typeof REALTIME_FEATURE_NAMES)[number]

/** Effective on/off state. The server always sends every key. */
export type RealtimeFeatures = Record<RealtimeFeatureName, boolean>

/** What `ready` says about whether this backend understands the switches. */
export type RealtimeFeaturesReady =
  | { supported: false }
  | { supported: true; features: RealtimeFeatures }

const STORAGE_PREFIX = 'realtime-features:'

/**
 * Accept a feature map only when every known key is a real boolean.
 *
 * A missing key, a stray key's absence, or `null` / `"false"` must not be
 * forwarded: `config.features` closes the socket on a bad value, and
 * `features.update` rejects the whole message.
 *
 * @param value Untrusted object from `ready`, an ack, or sessionStorage.
 * @returns The flags, or null when the payload is not a complete map.
 */
export function parseRealtimeFeatures(value: unknown): RealtimeFeatures | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const features = {} as RealtimeFeatures
  for (const name of REALTIME_FEATURE_NAMES) {
    if (typeof record[name] !== 'boolean') return null
    features[name] = record[name]
  }
  return features
}

/**
 * Read `ready.features`. A missing field means this backend has no switches.
 *
 * @param event The `ready` frame, which may predate the field.
 */
export function readReadyFeatures(event: { features?: unknown }): RealtimeFeaturesReady {
  if (!('features' in event) || event.features === undefined) return { supported: false }
  const features = parseRealtimeFeatures(event.features)
  if (!features) return { supported: false }
  return { supported: true, features }
}

/**
 * Keys whose value changed, shaped for `features.update`.
 *
 * @param from Last state the server acknowledged.
 * @param to What the panes should be now.
 * @returns The changed flags, or null when nothing changed.
 */
export function realtimeFeatureDiff(
  from: RealtimeFeatures,
  to: RealtimeFeatures
): Partial<RealtimeFeatures> | null {
  const partial: Partial<RealtimeFeatures> = {}
  for (const name of REALTIME_FEATURE_NAMES) {
    if (from[name] !== to[name]) partial[name] = to[name]
  }
  return Object.keys(partial).length > 0 ? partial : null
}

/**
 * Keep only boolean flags this client is allowed to send.
 *
 * @param value A partial update from the UI.
 * @returns A sendable partial, or null when it is empty or contains a non-boolean.
 */
export function sanitizeFeatureUpdate(
  value: Partial<RealtimeFeatures>
): Partial<RealtimeFeatures> | null {
  const partial: Partial<RealtimeFeatures> = {}
  for (const name of REALTIME_FEATURE_NAMES) {
    const flag = value[name]
    if (flag === undefined) continue
    if (typeof flag !== 'boolean') return null
    partial[name] = flag
  }
  return Object.keys(partial).length > 0 ? partial : null
}

/**
 * Opening state for a meeting: the folded panes from last time, otherwise the layout.
 *
 * Assist and Enhanced start off. Copilot and the Enhanced column both open
 * closed, and the first `config` says so. Opening either one turns its switch on.
 *
 * @param input Layout defaults plus anything remembered for this meeting.
 */
export function initialRealtimeFeatures(input: {
  memoOpen: boolean
  keyPointsOpen: boolean
  batchOpen: boolean
  stored: RealtimeFeatures | null
}): RealtimeFeatures {
  if (input.stored) return input.stored
  return {
    mainContent: input.memoOpen,
    keyPoints: input.keyPointsOpen,
    confirmItems: input.keyPointsOpen,
    enhanced: input.batchOpen,
    assist: false,
  }
}

/** Whether the Key Points column, which also holds Confirm Items, should be open. */
export function keyPointsColumnOpen(features: RealtimeFeatures): boolean {
  return features.keyPoints || features.confirmItems
}

/**
 * Remember which panes this meeting had folded, so a reload sends the same `config.features`.
 *
 * @param meetingId Room or meeting the socket is opened for.
 * @returns The stored map, or null when nothing was saved or it cannot be read.
 */
export function loadRealtimeFeatures(meetingId: string): RealtimeFeatures | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + meetingId)
    if (!raw) return null
    return parseRealtimeFeatures(JSON.parse(raw) as unknown)
  } catch {
    return null
  }
}

/**
 * Persist the panes the reader left folded. Storage failures are ignored.
 *
 * @param meetingId Room or meeting the socket is opened for.
 * @param features Flags to send again on the next `config`.
 */
export function saveRealtimeFeatures(meetingId: string, features: RealtimeFeatures): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + meetingId, JSON.stringify(features))
  } catch {
    // Private mode and a full quota both throw; the live panes still fold.
  }
}
